import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Button } from 'src/UIKit/components';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import React, { FC } from 'react';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import { TableHeader } from 'src/components/TableHeader/TableHeader';
import { isActionAllowed } from 'src/helpers/permissions';
import { getDateTimeUSAFormatWithTime } from 'src/constants/helpers';
import { useIntegrationsViewController } from '../../controllers';
import { formatDeliveryResult, formatEventType, formatPauseDescription, settingsTabPanelSx } from '../../helpers';
import { Integration, IntegrationDeliveryState } from '../../types';
import { AddIntegrationModal } from '../AddIntegrationModal';
import { HookDeliveryLogDialog } from '../HookDeliveryLogDialog';

const NO_DELIVERY: Omit<IntegrationDeliveryState, 'pauseThreshold'> = {
  consecutiveFailures: 0,
  lastStatusCode: null,
  lastErrorKind: null,
  dateLastAttempt: null,
};

type IntegrationsViewProps = {
  profileRole: string;
};

export const IntegrationsView: FC<IntegrationsViewProps> = ({ profileRole }) => {
  const {
    isLoading,
    isButtonsDisabled,
    searchQuery,
    sortField,
    page,
    itemsPerPage,
    integrations,
    changeSearch,
    changeSorting,
    changePagination,
    openCreateModal,
    openEditModal,
    removeIntegration,
    pause,
    activate,
    selectedCompanyId,
    deliveryLogHookId,
    openDeliveryLog,
    closeDeliveryLog,
    refreshIntegrations,
    addIntegrationModalController,
  } = useIntegrationsViewController(profileRole);
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const formatTableRows = (items: Integration[]) => {
    return items.map(({ id, name, status, event_type, url, delivery, pause }) => ({
      id,
      name,
      status,
      eventType: event_type,
      url,
      delivery,
      pause,
      actions: 'edit/delete',
    }));
  };

  return (
    <>
      <AddIntegrationModal {...addIntegrationModalController} />
      <Box sx={settingsTabPanelSx}>
        <GridData
          header={
            <TableHeader
              searchValue={searchQuery}
              onSearchChange={changeSearch}
              actions={
                <Button
                  variant="contained"
                  onClick={openCreateModal}
                  disabled={
                    !isActionAllowed({ authorRole: profileRole }) ||
                    profileRole === 'company_manager'
                  }
                >
                  Add Webhook
                </Button>
              }
            />
          }
          isLoading={isLoading}
          rows={formatTableRows(integrations.items || [])}
          columns={[
            {
              field: 'name',
              headerName: 'Name',
              minWidth: 200,
              editable: false,
              flex: 1,
            },
            {
              field: 'eventType',
              headerName: 'Event Type',
              minWidth: 200,
              editable: false,
              flex: 1,
              renderCell: r => <div>{formatEventType(r.value)}</div>,
            },
            {
              field: 'status',
              headerName: 'Status',
              minWidth: 120,
              editable: false,
              sortable: false,
              renderCell: ({ row }) =>
                row.status === 'paused' ? (
                  <Tooltip title={row.pause ? formatPauseDescription(row.pause, row.delivery?.consecutiveFailures ?? 0) : ''}>
                    <Chip size="small" color="warning" variant="outlined" label="Paused" />
                  </Tooltip>
                ) : (
                  <Chip size="small" color="success" variant="outlined" label="Active" />
                ),
            },
            {
              field: 'delivery',
              headerName: 'Last Delivery',
              minWidth: 260,
              editable: false,
              sortable: false,
              flex: 1.5,
              renderCell: ({ row }) => {
                const delivery = row.delivery ?? NO_DELIVERY;
                return (
                  <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
                    {delivery.dateLastAttempt ? (
                      <Typography variant="body2" noWrap>
                        {formatDeliveryResult({ statusCode: delivery.lastStatusCode, errorKind: delivery.lastErrorKind })}
                        <Typography variant="body2" component="span" color="text.secondary">
                          {` · ${getDateTimeUSAFormatWithTime(new Date(delivery.dateLastAttempt))}`}
                        </Typography>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        No deliveries yet
                      </Typography>
                    )}
                    {row.status === 'active' && delivery.consecutiveFailures > 0 && (
                      <Typography variant="caption" color="error.main" noWrap>
                        {delivery.consecutiveFailures} failed in a row
                      </Typography>
                    )}
                  </Stack>
                );
              },
            },
          ]}
          renderAction={r => (
            <>
              <Tooltip title="Delivery log">
                <IconButton size="small" color="primary" aria-label="Delivery log" onClick={() => openDeliveryLog(r.id)}>
                  <HistoryIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              {r.status === 'active' && (
                <Tooltip title="Pause">
                  <span>
                    <IconButton
                      disabled={isButtonsDisabled}
                      size="small"
                      color="primary"
                      aria-label="Pause"
                      onClick={() => pause(r.id)}
                    >
                      <PauseIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {r.status === 'paused' && (
                <Tooltip title="Activate">
                  <span>
                    <IconButton
                      disabled={isButtonsDisabled}
                      size="small"
                      color="primary"
                      aria-label="Activate"
                      onClick={() => activate(r.id)}
                    >
                      <PlayArrowIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton
                disabled={isButtonsDisabled}
                size="small"
                color="primary"
                onClick={() => openEditModal(r.id)}
              >
                <EditIcon fontSize="inherit" />
              </IconButton>
              <IconButton
                disabled={isButtonsDisabled}
                size="small"
                color="primary"
                onClick={() =>
                  openDialog({
                    title: `Delete "${r.name}"`,
                    content: 'Are you sure you want to remove this integration from your company?',
                    onConfirm: () => removeIntegration(r.id),
                  })
                }
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </>
          )}
          sorting={{
            value: sortField,
            onChange: (s: GridSortModel) => changeSorting(s),
          }}
          pagination={{
            page,
            pageSize: itemsPerPage,
            totalCount: integrations.count,
            onChange: (p: GridPaginationModel) => changePagination(p),
          }}
        />
      </Box>
      <ConfirmationDialog />
      <HookDeliveryLogDialog
        hookId={deliveryLogHookId}
        companyId={selectedCompanyId ?? ''}
        canEdit={!isButtonsDisabled}
        onClose={closeDeliveryLog}
        onHookChanged={refreshIntegrations}
      />
    </>
  );
};
